import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { SegmentedControl } from "@/components/segmented-control";
import { type StartViewAs, useStartViewAs, useViewAsOptions } from "@/lib/api";
import { apiErrorMessage, isToastedError } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { toast } from "@/lib/toast";
import { reloadDashboard } from "@/lib/view-as";

import { SettingsSection } from "../settings-section";
import { ViewAsPeopleTable } from "./people-table";
import { ViewAsRoleTable } from "./role-table";
import { type ViewAsChoice, viewAsChoice, withChoice } from "./utils";

/** Where a view starts: Home, the first page anyone sees. */
const START_PATH = "/home";

/**
 * Settings → Bekijken als (developers, `/settings/view-as`): view the whole
 * dashboard as a colleague sees it, or as a role, read-only, with the bar
 * above every page to switch or stop. "Een persoon | Een rol" chooses the
 * list (`?as=rol` for the roles, so a link keeps it): everyone with an
 * account but the developer, or Lid, Beheerder and Eigenaar. Starting one
 * opens Home with a full load, so every query starts afresh for the view,
 * where the bar says whom it shows; a refusal says why in an error toast.
 * Stoppen in the bar comes back here.
 */
export function ViewAsSettings() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const choice = viewAsChoice(params);
  const start = useStartViewAs();

  const view = (target: StartViewAs) => {
    // Started: the page is about to load Home for the view.
    if (start.isPending || start.isSuccess) {
      return;
    }
    logger.debug("[view-as]", "start →", target.person_id ? "person" : target.role);
    start.mutateAsync(target).then(
      () => reloadDashboard(START_PATH),
      (error: unknown) => {
        logger.warn("[view-as] start refused", error);
        if (!isToastedError(error)) {
          toast.error(() => apiErrorMessage(error, "startViewAs"));
        }
      },
    );
  };

  return (
    <SettingsSection title={t("nav.viewAs")} description={t("viewAs.description")}>
      <div className="space-y-4">
        <SegmentedControl<ViewAsChoice>
          label={t("viewAs.choice")}
          segments={[
            { value: "person", label: t("viewAs.person") },
            { value: "role", label: t("viewAs.role") },
          ]}
          value={choice}
          onChange={(next) => setParams(withChoice(params, next), { replace: true })}
        />
        {choice === "person" ? <PeopleList onView={view} /> : <ViewAsRoleTable onView={view} />}
      </div>
    </SettingsSection>
  );
}

/** The people half: the table once the list is in, else its loading or failure. */
function PeopleList({ onView }: { onView: (target: StartViewAs) => void }) {
  const { t } = useTranslation();
  const options = useViewAsOptions();
  if (options.isPending) {
    return <LoadingCard />;
  }
  if (options.isError) {
    return <LoadErrorCard message={t("viewAs.loadError")} onRetry={() => void options.refetch()} />;
  }
  return <ViewAsPeopleTable people={options.data?.people ?? []} onView={onView} />;
}
